import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar, 
  Send, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { planningService, communicationService } from '../../services/api';
import { RendezVous } from '../../types';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function AppointmentReminders() {
  const [selectedDate, setSelectedDate] = useState<string>(
    format(addDays(new Date(), 1), 'yyyy-MM-dd') // Demain par défaut
  );
  const [isSending, setIsSending] = useState(false);
  const queryClient = useQueryClient();

  // Récupérer les RDV pour la date sélectionnée
  const { data: rdvData, isLoading } = useQuery({
    queryKey: ['rdv-reminders', selectedDate],
    queryFn: () => planningService.getRendezVous({
      date_debut: selectedDate,
      date_fin: selectedDate
    }).then(res => res.data)
  });

  const rendezVous = rdvData?.results || [];
  const eligibleRdv = rendezVous.filter(rdv => 
    ['attente', 'confirme'].includes(rdv.status)
  );

  // Récupérer les templates de rappel
  const { data: templates } = useQuery({
    queryKey: ['reminder-templates'],
    queryFn: () => communicationService.getTemplatesByType('appointment_reminder').then(res => res.data)
  });

  // Récupérer les préférences des patients
  const { data: stats } = useQuery({
    queryKey: ['reminder-stats', selectedDate],
    queryFn: async () => {
      const preferences = await Promise.all(
        eligibleRdv.map(async (rdv) => {
          try {
            const prefs = await communicationService.getPreferencesByPatient(rdv.patient);
            return { rdvId: rdv.id, prefs: prefs.data };
          } catch {
            return { 
              rdvId: rdv.id, 
              prefs: {
                appointment_reminders_email: true,
                appointment_reminders_sms: false,
                appointment_reminders_whatsapp: false
              } 
            };
          }
        })
      );
      
      return preferences.reduce((acc, item) => {
        acc[item.rdvId] = item.prefs;
        return acc;
      }, {} as Record<number, any>);
    },
    enabled: eligibleRdv.length > 0
  });

  const handleSendReminders = async () => {
    if (!templates || templates.length === 0) {
      alert('Aucun template de rappel disponible');
      return;
    }

    setIsSending(true);
    try {
      const response = await communicationService.sendAppointmentReminders();
      alert(response.data.message);
      queryClient.invalidateQueries({ queryKey: ['communication-stats'] });
      queryClient.invalidateQueries({ queryKey: ['recent-logs'] });
    } catch (error) {
      console.error('Erreur envoi rappels:', error);
      alert('Erreur lors de l\'envoi des rappels');
    } finally {
      setIsSending(false);
    }
  };

  const getPatientCommunicationMethods = (rdvId: number) => {
    const prefs = stats?.[rdvId];
    if (!prefs) return [];

    const methods = [];
    if (prefs.appointment_reminders_email) methods.push('email');
    if (prefs.appointment_reminders_sms) methods.push('sms');
    if (prefs.appointment_reminders_whatsapp) methods.push('whatsapp');
    
    return methods;
  };

  const getTotalReminders = () => {
    if (!stats) return 0;
    return Object.values(stats).reduce((total: number, prefs: any) => {
      let count = 0;
      if (prefs.appointment_reminders_email) count++;
      if (prefs.appointment_reminders_sms) count++;
      if (prefs.appointment_reminders_whatsapp) count++;
      return total + count;
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec sélecteur de date */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rappels de rendez-vous</h2>
            <p className="text-gray-600">Envoyez des rappels automatiques à vos patients</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date des RDV</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="px-3 py-2 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            
            <button
              onClick={handleSendReminders}
              disabled={isSending || eligibleRdv.length === 0 || !templates?.length}
              className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isSending ? 'Envoi...' : 'Envoyer les rappels'}
            </button>
          </div>
        </div>

        {/* Résumé */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Calendar className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-800">RDV total</p>
                <p className="text-2xl font-bold text-blue-900">{rendezVous.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-800">RDV éligibles</p>
                <p className="text-2xl font-bold text-green-900">{eligibleRdv.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Send className="h-6 w-6 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-800">Messages à envoyer</p>
                <p className="text-2xl font-bold text-purple-900">{getTotalReminders()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Templates actifs</p>
                <p className="text-2xl font-bold text-yellow-900">{templates?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des rendez-vous */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Rendez-vous du {format(new Date(selectedDate), 'EEEE d MMMM yyyy', { locale: fr })}
          </h3>
        </div>

        {eligibleRdv.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">Aucun rendez-vous éligible</p>
            <p>Aucun rendez-vous trouvé pour cette date ou tous les rappels ont déjà été envoyés.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {eligibleRdv.map((rdv) => {
              const methods = getPatientCommunicationMethods(rdv.id);
              
              return (
                <div key={rdv.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {format(new Date(rdv.date_heure), 'HH:mm')}
                        </div>
                        <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                          rdv.status === 'confirme' ? 'bg-green-100 text-green-800' :
                          rdv.status === 'attente' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {rdv.status === 'confirme' ? 'Confirmé' :
                           rdv.status === 'attente' ? 'En attente' : rdv.status}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 mb-2">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {rdv.patient_details?.nom} {rdv.patient_details?.prenoms}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {rdv.doctor_details?.user_details 
                            ? `Dr. ${rdv.doctor_details.user_details.first_name} ${rdv.doctor_details.user_details.last_name}`
                            : 'Médecin non assigné'
                          }
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <strong>Motif:</strong> {rdv.motif}
                      </div>
                    </div>

                    {/* Méthodes de communication */}
                    <div className="flex items-center space-x-2">
                      {methods.length === 0 ? (
                        <span className="text-sm text-gray-500 px-3 py-1 bg-gray-100 rounded-full">
                          Pas de rappel configuré
                        </span>
                      ) : (
                        <>
                          <span className="text-sm text-gray-600 mr-2">Rappel via:</span>
                          {methods.map((method) => (
                            <span
                              key={method}
                              className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                            >
                              {method === 'email' && <Mail className="h-3 w-3 mr-1" />}
                              {method === 'sms' && <Phone className="h-3 w-3 mr-1" />}
                              {method === 'whatsapp' && <MessageSquare className="h-3 w-3 mr-1" />}
                              {method === 'email' && 'Email'}
                              {method === 'sms' && 'SMS'}
                              {method === 'whatsapp' && 'WhatsApp'}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Informations sur les templates */}
      {templates && templates.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Template utilisé</h4>
              <p className="text-sm text-blue-700">
                Les rappels seront envoyés en utilisant le template: <strong>"{templates[0].name}"</strong>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Sujet: {templates[0].subject}
              </p>
            </div>
          </div>
        </div>
      )}

      {(!templates || templates.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-yellow-800 mb-1">Aucun template de rappel</h4>
              <p className="text-sm text-yellow-700">
                Vous devez créer un template de type "Rappel de RDV" pour pouvoir envoyer des rappels automatiques.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}